import React, { useState } from 'react';
import { IoClose } from 'react-icons/io5';
import { showApiError } from '@/utils/toast';

interface UniversityCreateData {
  name: string;
  ai_uni_level_specific_advice: string;
  acceptance_rate: number;
  net_price_per_year: number;
  undergrad_students: number;
  application_deadline: string;
  admissions_website: string;
  location: string;
  environment: string;
  sat_range_min?: number;
  sat_range_max?: number;
  act_range_min?: number;
  act_range_max?: number;
  sat_act_status?: string;
  high_school_gpa?: string;
  early_decision_early_action?: string;
  application_fee?: number;
  in_state_tuition?: number;
  out_of_state_tuition?: number;
  average_total_aid_awarded?: number;
  students_receiving_financial_aid?: number;
  average_housing_cost?: number;
  average_meal_plan_cost?: number;
  books_and_supplies?: number;
  offers_online_courses?: string;
  graduation_rate?: number;
  employed_2_years_after_graduation?: number;
  median_earnings_6_years_after_graduation?: number;
  student_faculty_ratio?: number;
}

interface UniversityCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (universityData: UniversityCreateData) => Promise<void>;
}

const UniversityCreateModal: React.FC<UniversityCreateModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<UniversityCreateData>({
    name: '',
    ai_uni_level_specific_advice: '',
    acceptance_rate: 0,
    net_price_per_year: 0,
    undergrad_students: 0,
    application_deadline: '',
    admissions_website: '',
    location: '',
    environment: '',
    sat_range_min: undefined,
    sat_range_max: undefined,
    act_range_min: undefined,
    act_range_max: undefined,
    sat_act_status: '',
    high_school_gpa: '',
    early_decision_early_action: '',
    application_fee: undefined,
    in_state_tuition: undefined,
    out_of_state_tuition: undefined,
    average_total_aid_awarded: undefined,
    students_receiving_financial_aid: undefined,
    average_housing_cost: undefined,
    average_meal_plan_cost: undefined,
    books_and_supplies: undefined,
    offers_online_courses: '',
    graduation_rate: undefined,
    employed_2_years_after_graduation: undefined,
    median_earnings_6_years_after_graduation: undefined,
    student_faculty_ratio: undefined,
  });

  const handleInputChange = (field: keyof UniversityCreateData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const getFieldClassName = (fieldName: string, baseClassName: string) => {
    const hasError = errors[fieldName];
    const errorClass = hasError ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-transparent';
    return `${baseClassName} ${errorClass}`;
  };

  const validateForm = () => {
    const validationErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      validationErrors.name = 'University name is required';
    }
    
    if (!formData.location.trim()) {
      validationErrors.location = 'Location is required';
    }
    
    if (!formData.environment.trim()) {
      validationErrors.environment = 'Environment is required';
    }
    
    if (!formData.application_deadline.trim()) {
      validationErrors.application_deadline = 'Application deadline is required';
    }
    
    if (!formData.admissions_website.trim()) {
      validationErrors.admissions_website = 'Admissions website is required';
    }
    
    if (!formData.ai_uni_level_specific_advice.trim()) {
      validationErrors.ai_uni_level_specific_advice = 'AI-specific advice is required';
    }
    
    if (formData.acceptance_rate < 0 || formData.acceptance_rate > 100) {
      validationErrors.acceptance_rate = 'Acceptance rate must be between 0 and 100';
    }
    
    if (formData.net_price_per_year < 0) {
      validationErrors.net_price_per_year = 'Net price per year must be a positive number';
    }
    
    if (formData.undergrad_students < 0) {
      validationErrors.undergrad_students = 'Number of undergraduate students must be a positive number';
    }
    
    if (formData.sat_range_min && (formData.sat_range_min < 400 || formData.sat_range_min > 1600)) {
      validationErrors.sat_range_min = 'SAT minimum must be between 400 and 1600';
    }
    
    if (formData.sat_range_max && (formData.sat_range_max < 400 || formData.sat_range_max > 1600)) {
      validationErrors.sat_range_max = 'SAT maximum must be between 400 and 1600';
    }
    
    if (formData.sat_range_min && formData.sat_range_max && formData.sat_range_min > formData.sat_range_max) {
      validationErrors.sat_range_max = 'SAT maximum must be greater than minimum';
    }
    
    if (formData.act_range_min && (formData.act_range_min < 1 || formData.act_range_min > 36)) {
      validationErrors.act_range_min = 'ACT minimum must be between 1 and 36';
    }
    
    if (formData.act_range_max && (formData.act_range_max < 1 || formData.act_range_max > 36)) {
      validationErrors.act_range_max = 'ACT maximum must be between 1 and 36';
    }
    
    if (formData.act_range_min && formData.act_range_max && formData.act_range_min > formData.act_range_max) {
      validationErrors.act_range_max = 'ACT maximum must be greater than minimum';
    }
    
    return validationErrors;
  };

  const handleSave = async () => {
    const validationErrors = validateForm();
    
    setErrors(validationErrors);
    
    if (Object.keys(validationErrors).length > 0) {
      return;
    }
    
    try {
      setIsSaving(true);
      await onSave(formData);
      
      setFormData({
        name: '',
        ai_uni_level_specific_advice: '',
        acceptance_rate: 0,
        net_price_per_year: 0,
        undergrad_students: 0,
        application_deadline: '',
        admissions_website: '',
        location: '',
        environment: '',
        sat_range_min: undefined,
        sat_range_max: undefined,
        act_range_min: undefined,
        act_range_max: undefined,
        sat_act_status: '',
        high_school_gpa: '',
        early_decision_early_action: '',
        application_fee: undefined,
        in_state_tuition: undefined,
        out_of_state_tuition: undefined,
        average_total_aid_awarded: undefined,
        students_receiving_financial_aid: undefined,
        average_housing_cost: undefined,
        average_meal_plan_cost: undefined,
        books_and_supplies: undefined,
        offers_online_courses: '',
        graduation_rate: undefined,
        employed_2_years_after_graduation: undefined,
        median_earnings_6_years_after_graduation: undefined,
        student_faculty_ratio: undefined,
      });
      
      onClose();
    } catch (error: any) {
      showApiError(error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col">
        
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-6 text-white relative flex-shrink-0 rounded-t-2xl">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="absolute top-6 right-8 text-white hover:text-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {IoClose({ className: "text-xl" })}
          </button>
          <h2 className="text-2xl font-bold">Add New University</h2>
          <p className="text-blue-100 mt-1">Create a new university profile</p>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="p-8 space-y-8">
            
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-6">Basic Information</h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">University Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={isSaving}
                    className={getFieldClassName('name', 'w-full px-4 py-3 border rounded-lg focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed')}
                    placeholder="University name"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Location *</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    disabled={isSaving}
                    className={getFieldClassName('location', 'w-full px-4 py-3 border rounded-lg focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed')}
                    placeholder="City, State"
                  />
                  {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Environment *</label>
                  <select
                    value={formData.environment}
                    onChange={(e) => handleInputChange('environment', e.target.value)}
                    disabled={isSaving}
                    className={getFieldClassName('environment', 'w-full px-4 py-3 border rounded-lg focus:ring-2 bg-white disabled:opacity-50 disabled:cursor-not-allowed')}
                  >
                    <option value="">Select environment</option>
                    <option value="Urban">Urban</option>
                    <option value="Suburban">Suburban</option>
                    <option value="Rural">Rural</option>
                  </select>
                  {errors.environment && <p className="text-red-500 text-sm mt-1">{errors.environment}</p>}
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Admissions Website *</label>
                  <input
                    type="url"
                    value={formData.admissions_website}
                    onChange={(e) => handleInputChange('admissions_website', e.target.value)}
                    disabled={isSaving}
                    className={getFieldClassName('admissions_website', 'w-full px-4 py-3 border rounded-lg focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed')}
                    placeholder="https://admissions.university.edu"
                  />
                  {errors.admissions_website && <p className="text-red-500 text-sm mt-1">{errors.admissions_website}</p>}
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-6">Academic Information</h3>
              <div className="grid grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Acceptance Rate (%) *</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.acceptance_rate}
                    onChange={(e) => handleInputChange('acceptance_rate', parseFloat(e.target.value) || 0)}
                    disabled={isSaving}
                    className={getFieldClassName('acceptance_rate', 'w-full px-4 py-3 border rounded-lg focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed')}
                  />
                  {errors.acceptance_rate && <p className="text-red-500 text-sm mt-1">{errors.acceptance_rate}</p>}
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Undergraduate Students *</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.undergrad_students}
                    onChange={(e) => handleInputChange('undergrad_students', parseInt(e.target.value) || 0)}
                    disabled={isSaving}
                    className={getFieldClassName('undergrad_students', 'w-full px-4 py-3 border rounded-lg focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed')}
                  />
                  {errors.undergrad_students && <p className="text-red-500 text-sm mt-1">{errors.undergrad_students}</p>}
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Application Deadline *</label>
                  <input
                    type="date"
                    value={formData.application_deadline}
                    onChange={(e) => handleInputChange('application_deadline', e.target.value)}
                    disabled={isSaving}
                    className={getFieldClassName('application_deadline', 'w-full px-4 py-3 border rounded-lg focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed')}
                  />
                  {errors.application_deadline && <p className="text-red-500 text-sm mt-1">{errors.application_deadline}</p>}
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-6">Test Scores</h3>
              <div className="grid grid-cols-4 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">SAT Min</label>
                  <input
                    type="number"
                    min="400"
                    max="1600"
                    value={formData.sat_range_min || ''}
                    onChange={(e) => handleInputChange('sat_range_min', e.target.value ? parseInt(e.target.value) : undefined)}
                    disabled={isSaving}
                    className={getFieldClassName('sat_range_min', 'w-full px-4 py-3 border rounded-lg focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed')}
                  />
                  {errors.sat_range_min && <p className="text-red-500 text-sm mt-1">{errors.sat_range_min}</p>}
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">SAT Max</label>
                  <input
                    type="number"
                    min="400"
                    max="1600"
                    value={formData.sat_range_max || ''}
                    onChange={(e) => handleInputChange('sat_range_max', e.target.value ? parseInt(e.target.value) : undefined)}
                    disabled={isSaving}
                    className={getFieldClassName('sat_range_max', 'w-full px-4 py-3 border rounded-lg focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed')}
                  />
                  {errors.sat_range_max && <p className="text-red-500 text-sm mt-1">{errors.sat_range_max}</p>}
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">ACT Min</label>
                  <input
                    type="number"
                    min="1"
                    max="36"
                    value={formData.act_range_min || ''}
                    onChange={(e) => handleInputChange('act_range_min', e.target.value ? parseInt(e.target.value) : undefined)}
                    disabled={isSaving}
                    className={getFieldClassName('act_range_min', 'w-full px-4 py-3 border rounded-lg focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed')}
                  />
                  {errors.act_range_min && <p className="text-red-500 text-sm mt-1">{errors.act_range_min}</p>}
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">ACT Max</label>
                  <input
                    type="number"
                    min="1"
                    max="36"
                    value={formData.act_range_max || ''}
                    onChange={(e) => handleInputChange('act_range_max', e.target.value ? parseInt(e.target.value) : undefined)}
                    disabled={isSaving}
                    className={getFieldClassName('act_range_max', 'w-full px-4 py-3 border rounded-lg focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed')}
                  />
                  {errors.act_range_max && <p className="text-red-500 text-sm mt-1">{errors.act_range_max}</p>}
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-6">Financial Information</h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Net Price Per Year ($) *</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.net_price_per_year}
                    onChange={(e) => handleInputChange('net_price_per_year', parseInt(e.target.value) || 0)}
                    disabled={isSaving}
                    className={getFieldClassName('net_price_per_year', 'w-full px-4 py-3 border rounded-lg focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed')}
                  />
                  {errors.net_price_per_year && <p className="text-red-500 text-sm mt-1">{errors.net_price_per_year}</p>}
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Application Fee ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.application_fee || ''}
                    onChange={(e) => handleInputChange('application_fee', e.target.value ? parseInt(e.target.value) : undefined)}
                    disabled={isSaving}
                    className={getFieldClassName('application_fee', 'w-full px-4 py-3 border rounded-lg focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed')}
                  />
                  {errors.application_fee && <p className="text-red-500 text-sm mt-1">{errors.application_fee}</p>}
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-6">AI-Specific Advice</h3>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">University-Specific AI Advice *</label>
                <textarea
                  value={formData.ai_uni_level_specific_advice}
                  onChange={(e) => handleInputChange('ai_uni_level_specific_advice', e.target.value)}
                  disabled={isSaving}
                  className={getFieldClassName('ai_uni_level_specific_advice', 'w-full px-4 py-3 border rounded-lg focus:ring-2 resize-none h-24 disabled:opacity-50 disabled:cursor-not-allowed')}
                  placeholder="Provide specific advice for AI models when working with this university's requirements..."
                />
                {errors.ai_uni_level_specific_advice && <p className="text-red-500 text-sm mt-1">{errors.ai_uni_level_specific_advice}</p>}
              </div>
            </div>

          </div>
        </div>

        <div className="flex-shrink-0 border-t border-gray-200 px-8 py-4 bg-white rounded-b-2xl">
          <div className="flex justify-end space-x-4">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSaving && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <span>{isSaving ? 'Creating...' : 'Create University'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UniversityCreateModal;